'use client';

import { useCallback, useMemo } from 'react';

import { useKlaviyo } from '@/hooks';
import { type LogParameters, type Logger, useLoggerRootContext } from '@skin-seoul/utils';

export const useKlaviyoLogger = (pageName: string): Logger => {
  const { appName, metadata, isInitialized } = useLoggerRootContext();
  const { track } = useKlaviyo();

  const view = useCallback<Logger['view']>(
    (parameters?: LogParameters, _options?: { useKlaviyo?: boolean }): Promise<void> | null => {
      try {
        track('page_view', {
          page_title: pageName,
          app_name: appName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking klaviyo page view:', error);
        return null;
      }
    },
    [track, pageName, appName, metadata],
  );

  const event = useCallback<Logger['event']>(
    (title, action, parameters, _options): Promise<void> | null => {
      try {
        track(title, {
          action,
          pageName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking klaviyo event:', error);
        return null;
      }
    },
    [track, pageName, metadata],
  );

  const actionEvent = useMemo(() => {
    function createActionEvent(action: string) {
      return (name: string, parameters?: LogParameters, options?: { useKlaviyo?: boolean }) => {
        return event(name, action, parameters, options);
      };
    }

    return {
      click: createActionEvent('clicked'),
      confirmed: createActionEvent('confirmed'),
      created: createActionEvent('created'),
      updated: createActionEvent('updated'),
      selected: createActionEvent('selected'),
      filled: createActionEvent('filled'),
      impressed: createActionEvent('impressed'),
      unimpressed: createActionEvent('unimpressed'),
      searched: createActionEvent('searched'),
    };
  }, [event]);

  return useMemo(
    () => ({ view, event, isInitialized, ...actionEvent }) as Logger,
    [view, event, isInitialized, actionEvent],
  );
};
